'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { signInWithGoogle } from '@/lib/googleAuth';
import { onOAuthReturn } from '@/lib/oauthRetry';

interface SignUpFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

function EmailConfirmationNotice({ email, onSwitchToLogin }: { email: string; onSwitchToLogin: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
        <svg viewBox="0 0 24 24" className="w-8 h-8 fill-none stroke-green-600 stroke-2">
          <path d="M22 2L11 13" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 2L15 22L11 13L2 9L22 2Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-2">Check your email</h2>
      <p className="text-muted-foreground mb-2">
        We sent a confirmation link to
      </p>
      <p className="text-foreground font-semibold mb-6">{email}</p>
      <p className="text-sm text-muted-foreground mb-8">
        Click the link in the email to verify your account, then come back and sign in.
      </p>
      <button
        type="button"
        onClick={onSwitchToLogin}
        className="btn-primary w-full py-3 text-base"
      >
        Go to Sign In
      </button>
    </div>
  );
}

export default function SignUpForm({ onSwitchToLogin }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);

  // If the user cancels the Google flow and comes BACK to this page (browser
  // Back / bfcache restore), restore the button — otherwise it stays stuck on
  // "Redirecting…" and disabled until the app is fully reopened.
  useEffect(() => onOAuthReturn(() => setIsGoogleLoading(false)), []);

  const signUpWithGoogle = async () => {
    if (isGoogleLoading) return; // never start two OAuth redirects at once
    setIsGoogleLoading(true);
    try {
      // This redirects the current page to Google — the callback will
      // create the user account and redirect to onboarding.
      await signInWithGoogle('/onboarding');
      // If the redirect did NOT navigate (blocked, aborted by the browser),
      // restore the button instead of leaving a permanent loading state.
      setIsGoogleLoading(false);
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : 'Could not start Google sign-in.',
      });
      setIsGoogleLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
  });

  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.fullName,
          email: data.email,
          password: data.password,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to create your account.');

      // If Supabase has email confirmation disabled, the user gets a session
      // immediately — redirect to onboarding instead of showing the email screen.
      if (result.needsEmailConfirmation === false) {
        toast.success('Account created! Welcome to Kasnimo.', { duration: 3000 });
        router.push('/onboarding');
        return;
      }

      setConfirmationEmail(data.email);
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : 'Unable to create your account.',
      });
      setIsLoading(false);
    }
  };

  const passwordStrength = (pwd: string): { score: number; label: string; color: string } => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const map = [
      { score: 1, label: 'Weak', color: 'bg-danger' },
      { score: 2, label: 'Fair', color: 'bg-amber-500' },
      { score: 3, label: 'Good', color: 'bg-blue-500' },
      { score: 4, label: 'Strong', color: 'bg-green-500' },
    ];
    return map[score - 1] || { score, label: 'Weak', color: 'bg-danger' };
  };

  const strength = passwordStrength(passwordValue || '');

  if (confirmationEmail) {
    return <EmailConfirmationNotice email={confirmationEmail} onSwitchToLogin={onSwitchToLogin} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Start organizing your scholarship applications for free
        </p>
      </div>

      {/* Social auth */}
      <div className="mb-5">
        <button
          type="button"
          onClick={signUpWithGoogle}
          disabled={isGoogleLoading}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted transition-all active:scale-[0.98] disabled:opacity-60"
          style={{ minHeight: '48px' }}
        >
          {isGoogleLoading ? (
            <svg viewBox="0 0 24 24" className="w-4 h-4 animate-spin fill-none stroke-current stroke-2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {isGoogleLoading ? 'Redirecting…' : 'Continue with Google'}
        </button>
      </div>

      <div className="relative flex items-center mb-5">
        <div className="flex-1 border-t border-border" />
        <span className="px-3 text-xs text-muted-foreground bg-background">
          or register with email
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* Full Name */}
      <div className="mb-4">
        <label htmlFor="signup-name" className="block text-sm font-semibold text-foreground mb-1.5">
          Full name
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          {...register('fullName', {
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
          })}
          className={`input-base ${errors.fullName ? 'border-danger focus:ring-danger' : ''}`}
          placeholder="Amara Mensah"
          aria-describedby={errors.fullName ? 'signup-name-error' : undefined}
          aria-invalid={!!errors.fullName}
        />
        {errors.fullName && (
          <p id="signup-name-error" className="mt-1.5 text-xs text-danger">
            {errors.fullName.message}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="mb-4">
        <label
          htmlFor="signup-email"
          className="block text-sm font-semibold text-foreground mb-1.5"
        >
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address',
            },
          })}
          className={`input-base ${errors.email ? 'border-danger focus:ring-danger' : ''}`}
          placeholder="you@university.edu"
          aria-describedby={errors.email ? 'signup-email-error' : undefined}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p id="signup-email-error" className="mt-1.5 text-xs text-danger">
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div className="mb-4">
        <label
          htmlFor="signup-password"
          className="block text-sm font-semibold text-foreground mb-1.5"
        >
          Password
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">
          Minimum 8 characters with uppercase, number, and symbol
        </p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: {
                value: /^(?=.*[A-Z])(?=.*[0-9])/,
                message: 'Must include at least one uppercase letter and one number',
              },
            })}
            className={`input-base pr-11 ${errors.password ? 'border-danger focus:ring-danger' : ''}`}
            placeholder="Create a strong password"
            aria-describedby={errors.password ? 'signup-password-error' : undefined}
            aria-invalid={!!errors.password}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-3.5 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
        {/* Password strength meter */}
        {passwordValue && strength.score > 0 && (
          <div className="mt-2">
            <div className="flex gap-1 mb-1">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={`strength-${i}`}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    i <= strength.score ? strength.color : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <p
              className={`text-xs font-medium ${
                strength.score <= 1
                  ? 'text-danger'
                  : strength.score === 2
                    ? 'text-amber-600'
                    : strength.score === 3
                      ? 'text-blue-600'
                      : 'text-green-600'
              }`}
            >
              {strength.label} password
            </p>
          </div>
        )}
        {errors.password && (
          <p id="signup-password-error" className="mt-1.5 text-xs text-danger">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="mb-5">
        <label
          htmlFor="signup-confirm"
          className="block text-sm font-semibold text-foreground mb-1.5"
        >
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === passwordValue || 'Passwords do not match',
            })}
            className={`input-base pr-11 ${errors.confirmPassword ? 'border-danger focus:ring-danger' : ''}`}
            placeholder="Repeat your password"
            aria-describedby={errors.confirmPassword ? 'signup-confirm-error' : undefined}
            aria-invalid={!!errors.confirmPassword}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute inset-y-0 right-3.5 flex items-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirm ? (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
                <path
                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p id="signup-confirm-error" className="mt-1.5 text-xs text-danger">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      {/* Terms */}
      <div className="mb-6">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            {...register('agreeToTerms', {
              required: 'You must agree to the terms to create an account',
            })}
            className="w-4 h-4 rounded border-input accent-primary cursor-pointer mt-0.5 flex-shrink-0"
          />
          <span className="text-sm text-muted-foreground leading-relaxed">
            I agree to the{' '}
            <span className="text-primary font-medium hover:text-blue-700 cursor-pointer">
              Terms of Service
            </span>{' '}
            and{' '}
            <span className="text-primary font-medium hover:text-blue-700 cursor-pointer">
              Privacy Policy
            </span>
          </span>
        </label>
        {errors.agreeToTerms && (
          <p className="mt-1.5 text-xs text-danger">{errors.agreeToTerms.message}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4 animate-spin fill-none stroke-current stroke-2"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Creating your account…
          </>
        ) : (
          'Create Free Account'
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:text-blue-700 transition-colors"
        >
          Sign in
        </button>
      </p>
    </form>
  );
}
